/**
 * Declarative `keys` sections of the preferences window (见 21 / 05): a module or
 * the platform lists setting keys, the window draws one row per key from its
 * `contributes.settings` declaration.
 *
 * - `boolean` → switch row; `enum` → select (labels from `enumTitles`, else the
 *   raw values); `string` / `number` → input that writes on blur or Enter.
 * - A key absent from the schema is not drawn: the configuration service only
 *   lists settings that exist on this runtime (见 05 `runtimes`).
 * - Writes are optimistic; a rejected write restores the previous value, logs
 *   and tells the user.
 */
import type { ChannelClient } from '@hibro/platform-core';
import type {
  ConfigChange,
  IConfigurationService,
  ResolvedSetting,
} from '@hibro/shared';
import { CONFIG_CHANGED_EVENT } from '@hibro/shared';
import {
  cn,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SETTINGS_CARD,
  SETTINGS_ROW,
  SettingsHairline,
  SettingsHint,
  SettingsSectionTitle,
  SettingsSwitchRow,
  toast,
} from '@hibro/ui-client';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { settingsWindowLogError } from './log';

export interface SettingsConfig {
  readonly status: 'loading' | 'ready' | 'error' | 'unavailable';
  readonly schema: ReadonlyMap<string, ResolvedSetting>;
  readonly values: Readonly<Record<string, unknown>>;
  readonly write: (key: string, value: unknown) => void;
}

/** Schema and values for every `keys` section of the window, kept live. */
export function useSettingsConfig(
  configService: IConfigurationService | undefined,
  client: ChannelClient | undefined,
  t: (key: string, args?: Record<string, unknown>) => string,
): SettingsConfig {
  const [status, setStatus] = useState<SettingsConfig['status']>(
    configService ? 'loading' : 'unavailable',
  );
  const [schema, setSchema] = useState<ReadonlyMap<string, ResolvedSetting>>(
    () => new Map(),
  );
  const [values, setValues] = useState<Record<string, unknown>>({});
  const valuesRef = useRef(values);
  valuesRef.current = values;

  useEffect(() => {
    if (!configService) {
      setStatus('unavailable');
      return;
    }
    let active = true;
    setStatus('loading');
    void Promise.all([configService.getSchema(), configService.getAll()])
      .then(([nextSchema, nextValues]) => {
        if (!active) {
          return;
        }
        setSchema(new Map(nextSchema.map((entry) => [entry.key, entry])));
        setValues(nextValues);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        settingsWindowLogError('settings window config load failed', {
          reason: err instanceof Error ? err.message : String(err),
        });
        if (active) {
          setStatus('error');
        }
      });
    return () => {
      active = false;
    };
  }, [configService]);

  useEffect(() => {
    if (!client) {
      return;
    }
    return client.listen(CONFIG_CHANGED_EVENT, (data) => {
      const change = data as ConfigChange;
      setValues((prev) => ({ ...prev, [change.key]: change.value }));
    });
  }, [client]);

  const write = useCallback(
    (key: string, value: unknown) => {
      if (!configService) {
        return;
      }
      const previous = valuesRef.current[key];
      setValues((prev) => ({ ...prev, [key]: value }));
      void configService.setValue(key, value).catch((err: unknown) => {
        const message = err instanceof Error ? err.message : String(err);
        settingsWindowLogError('settings window setting write failed', {
          key,
          reason: message,
        });
        setValues((prev) => ({ ...prev, [key]: previous }));
        toast(t('settingsWindow.saveFailed', { message }));
      });
    },
    [configService, t],
  );

  return useMemo(
    () => ({ status, schema, values, write }),
    [status, schema, values, write],
  );
}

function valueOf(entry: ResolvedSetting, values: SettingsConfig['values']) {
  return entry.key in values ? values[entry.key] : entry.default;
}

function TextRow({
  entry,
  label,
  description,
  value,
  last,
  onCommit,
}: {
  entry: ResolvedSetting;
  label: string;
  description?: string;
  value: unknown;
  last: boolean;
  onCommit: (next: unknown) => void;
}): ReactNode {
  const shown =
    typeof value === 'string' || typeof value === 'number' ? String(value) : '';
  const [draft, setDraft] = useState(shown);
  useEffect(() => setDraft(shown), [shown]);
  const numeric = entry.type === 'number';
  const parsed = numeric ? Number(draft) : draft;
  const invalid = numeric && (draft.trim() === '' || Number.isNaN(parsed));
  const commit = () => {
    if (invalid || draft === shown) {
      return;
    }
    onCommit(parsed);
  };
  return (
    <div className="flex w-full flex-col">
      <label className={cn(SETTINGS_ROW, 'gap-4 py-4')}>
        <RowLabel label={label} description={description} />
        <input
          type={numeric ? 'number' : 'text'}
          value={draft}
          aria-invalid={invalid || undefined}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              commit();
            }
            if (event.key === 'Escape') {
              setDraft(shown);
            }
          }}
          className={cn(
            'h-8 w-[220px] shrink-0 rounded-[10px] border bg-transparent px-2.5 text-[14px] leading-[22px] text-(--app-color-text-primary) outline-none focus-visible:border-(--app-base-brand)',
            invalid
              ? 'border-(--app-color-text-error)'
              : 'border-(--app-color-border)',
          )}
        />
      </label>
      {last ? null : <SettingsHairline />}
    </div>
  );
}

function EnumRow({
  label,
  description,
  options,
  value,
  last,
  onChange,
}: {
  label: string;
  description?: string;
  options: readonly { value: string; label: string }[];
  value: unknown;
  last: boolean;
  onChange: (next: string) => void;
}): ReactNode {
  return (
    <div className="flex w-full flex-col">
      <div className={cn(SETTINGS_ROW, 'gap-4 py-4')}>
        <RowLabel label={label} description={description} />
        <Select
          items={options}
          value={typeof value === 'string' ? value : null}
          onValueChange={(next) => {
            if (typeof next === 'string') {
              onChange(next);
            }
          }}
        >
          <SelectTrigger aria-label={label} className="w-[220px] shrink-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {last ? null : <SettingsHairline />}
    </div>
  );
}

function RowLabel({
  label,
  description,
}: {
  label: string;
  description?: string;
}): ReactNode {
  return (
    <span className="flex min-w-0 flex-col">
      <span className="text-[14px] leading-[22px] text-(--app-color-text-primary)">
        {label}
      </span>
      {description ? (
        <span className="text-[13px] leading-5 text-(--app-color-text-tertiary)">
          {description}
        </span>
      ) : null}
    </span>
  );
}

export function KeysSection({
  title,
  keys,
  config,
  label,
  t,
}: {
  title?: string;
  keys: readonly string[];
  config: SettingsConfig;
  /** Resolve a setting's `%key%` field against its declaring module. */
  label: (moduleId: string, raw: string) => string;
  t: (key: string) => string;
}): ReactNode {
  const entries = keys.flatMap((key) => {
    const entry = config.schema.get(key);
    return entry ? [entry] : [];
  });
  if (config.status === 'ready' && entries.length === 0) {
    return null;
  }
  const heading = title ? (
    <SettingsSectionTitle>{title}</SettingsSectionTitle>
  ) : null;
  if (config.status !== 'ready') {
    return (
      <div className="flex w-full flex-col">
        {heading}
        {config.status === 'loading' ? null : (
          <SettingsHint>
            {t(
              config.status === 'error'
                ? 'settingsWindow.configLoadFailed'
                : 'settingsWindow.unavailable',
            )}
          </SettingsHint>
        )}
      </div>
    );
  }
  return (
    <div className="flex w-full flex-col">
      {heading}
      <div className={SETTINGS_CARD}>
        {entries.map((entry, index) => {
          const last = index === entries.length - 1;
          const rowLabel = entry.title
            ? label(entry.moduleId, entry.title)
            : entry.key;
          const description = entry.description
            ? label(entry.moduleId, entry.description)
            : undefined;
          const value = valueOf(entry, config.values);
          const write = (next: unknown) => config.write(entry.key, next);
          switch (entry.type) {
            case 'boolean':
              return (
                <SettingsSwitchRow
                  key={entry.key}
                  label={rowLabel}
                  description={description}
                  checked={value === true}
                  last={last}
                  onChange={write}
                />
              );
            case 'enum':
              return (
                <EnumRow
                  key={entry.key}
                  label={rowLabel}
                  description={description}
                  options={(entry.enum ?? []).map((option, at) => {
                    const raw = entry.enumTitles?.[at];
                    return {
                      value: option,
                      label: raw ? label(entry.moduleId, raw) : option,
                    };
                  })}
                  value={value}
                  last={last}
                  onChange={write}
                />
              );
            case 'string':
            case 'number':
              return (
                <TextRow
                  key={entry.key}
                  entry={entry}
                  label={rowLabel}
                  description={description}
                  value={value}
                  last={last}
                  onCommit={write}
                />
              );
          }
        })}
      </div>
    </div>
  );
}
